import {
	DndContext,
	DragOverlay,
	PointerSensor,
	closestCorners,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, arrayMove } from '@dnd-kit/sortable';
import _ from 'lodash';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import { LocalizationScope } from '@treams/treams-hooks';
import { Empty, Input, LanguageSelect, Skeleton, Switch } from '@treams/treams-ui';

import { SearchIcon } from '../icons';
import { CreateLibraryCategory } from './CreateLibraryCategory';
import { LibraryCategory } from './LibraryCategory';
import { LibraryItem, LibraryItemLayoutScope } from './LibraryItem';
import { LibraryMutations } from './LibraryMutations';
import { LibraryRenderer } from './LibraryRenderer';
import { LibraryScorer } from './LibraryScorer';

function computeOrder<T extends { order: number }>(items: T[], index: number): number {
	if (items.length <= 1) {
		return 0;
	} else if (index === 0) {
		return items[1].order / 2;
	} else if (index === items.length - 1) {
		return items[index - 1].order * 2;
	} else {
		const previous = items[index - 1].order;
		const next = items[index + 1].order;

		return previous + (next - previous) / 2;
	}
}

function LibraryWithData<
	T extends { id: string; name?: string; order: number; isArchived: boolean },
	U extends { id: string; order: number; category: T },
>(props: {
	query?: string;
	archived?: boolean;
	categories: T[];
	items: U[];
	placeholder?: Partial<U>;
	scorer: LibraryScorer<T, U>;
	renderer: LibraryRenderer<T, U>;
	mutations: LibraryMutations<T, U>;
	onChangeSelectedItems?: (items: U[]) => void;
}) {
	const { t } = useTranslation();
	const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

	const [active, setActive] = useState<string | undefined>();

	const [categories, setCategories] = useState(
		_.sortBy(props.categories, 'order').map((category) => ({
			...category,
			items: _.sortBy(
				props.items.filter((item) => item.category.id === category.id),
				'order',
			),
		})),
	);

	const fix = useRef<Record<string, boolean>>({});

	const [reorderingCategories, setReorderingCategories] = useState<Record<string, boolean>>({});
	const [reorderingItems, setReorderingItems] = useState<Record<string, boolean>>({});
	const loadingItems = useMemo(() => Array.from(Object.keys(reorderingItems)), [reorderingItems]);

	const onUpdateCategory = useCallback((category: T) => {
		setCategories((categories) =>
			categories.map((existing) =>
				existing.id === category.id
					? {
							...existing,
							name: category.name,
						}
					: existing,
			),
		);
	}, []);

	const onForgetCategory = useCallback((category: T) => {
		setCategories((categories) => categories.filter(({ id }) => id !== category.id));
	}, []);

	const onForgetItem = useCallback((item: U) => {
		setCategories((categories) =>
			categories.map((category) => {
				if (category.id === item.category.id) {
					return {
						...category,
						items: category.items.filter(({ id }) => id !== item.id),
					};
				}
				return category;
			}),
		);
	}, []);

	const onDragStart = useCallback((event) => {
		setActive(event.active.id as string);
	}, []);

	const onDragOver = useCallback((event) => {
		if (typeof event.active.id !== 'string' || typeof event.over?.id !== 'string') {
			return;
		}

		const a = event.active.id;
		const b = event.over.id;

		const key = `${a}:${b}:${event.delta.x}:${event.delta.y}`;

		if (fix.current[key] === true) {
			return;
		}

		fix.current[key] = true;

		if (a.startsWith('category:') && b.startsWith('category:')) {
			setCategories((items) => {
				const oldIndex = items.findIndex(({ id }) => `category:${id}` === event.active.id);
				const newIndex = items.findIndex(({ id }) => `category:${id}` === event.over!.id);
				return arrayMove(items, oldIndex, newIndex);
			});
		} else if (
			a.startsWith('item:') &&
			(b.startsWith('category:') || b.startsWith('$placeholder:'))
		) {
			setCategories((categories) => {
				const copy = categories.slice();
				const sectionA = copy.findIndex(({ items }) => items.some(({ id }) => `item:${id}` === a));
				const indexA = copy[sectionA].items.findIndex(({ id }) => `item:${id}` === a);
				const sectionB = copy.findIndex(
					({ id }) => `category:${id}` === b || `$placeholder:${id}` === b,
				);
				const indexB = copy[sectionB].items.length;
				if (sectionA === sectionB) {
					return categories;
				}
				copy[sectionA] = { ...copy[sectionA], items: copy[sectionA].items.slice() };
				copy[sectionB] = { ...copy[sectionB], items: copy[sectionB].items.slice() };
				copy[sectionB].items.splice(indexB, 0, copy[sectionA].items.splice(indexA, 1)[0]);
				return copy;
			});
		} else if (a.startsWith('item:') && b.startsWith('item:')) {
			setCategories((categories) => {
				const copy = categories.slice();
				const sectionA = copy.findIndex(({ items }) => items.some(({ id }) => `item:${id}` === a));
				const indexA = copy[sectionA].items.findIndex(({ id }) => `item:${id}` === a);
				const sectionB = copy.findIndex(({ items }) => items.some(({ id }) => `item:${id}` === b));
				const indexB = copy[sectionB].items.findIndex(({ id }) => `item:${id}` === b);

				if (sectionA === sectionB && indexA === indexB) {
					return categories;
				}

				if (sectionA !== sectionB) {
					copy[sectionA] = { ...copy[sectionA], items: copy[sectionA].items.slice() };
					copy[sectionB] = { ...copy[sectionB], items: copy[sectionB].items.slice() };
					copy[sectionB].items.splice(indexB, 0, copy[sectionA].items.splice(indexA, 1)[0]);
				} else {
					copy[sectionA] = {
						...copy[sectionA],
						items: arrayMove(copy[sectionA].items, indexA, indexB),
					};
				}

				return copy;
			});
		}
	}, []);

	const onDragEnd = useCallback(
		async (event) => {
			setActive(undefined);
			fix.current = {};

			if (typeof event.active.id === 'string') {
				if (event.active.id.startsWith('category:')) {
					const index = categories.findIndex(({ id }) => `category:${id}` === event.active.id);
					const order = computeOrder(categories, index);

					setCategories((items) =>
						items.map((item) =>
							event.active.id === `category:${item.id}`
								? {
										...item,
										order,
									}
								: item,
						),
					);

					setReorderingCategories((existing) => ({
						...existing,
						[categories[index].id]: true,
					}));

					await props.mutations.reorderCategory?.(categories[index].id, order);

					setReorderingCategories((existing) => _.omit(existing, categories[index].id));
				} else if (event.active.id.startsWith('item:')) {
					const categoryIndex = categories.findIndex(({ items }) =>
						items.some(({ id }) => `item:${id}` === event.active.id),
					);
					const categoryID = categories[categoryIndex].id;
					const itemIndex = categories[categoryIndex].items.findIndex(
						({ id }) => `item:${id}` === event.active.id,
					);
					const id = categories[categoryIndex].items[itemIndex].id;

					const order = computeOrder(categories[categoryIndex].items, itemIndex);

					setCategories((categories) =>
						categories.map((category) => ({
							...category,
							items: category.items.map((item) =>
								`item:${item.id}` === event.active.id
									? {
											...item,
											order,
										}
									: item,
							),
						})),
					);

					setReorderingItems((items) => ({
						...items,
						[id]: true,
					}));

					await props.mutations.reorderItem?.(id, categoryID, order);

					setReorderingItems((items) => _.omit(items, id));
				}
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[categories],
	);

	const scorer = useMemo(
		() => ({
			category: (category: T) => props.scorer.category(category, props.query),
			item: (item: U) => props.scorer.item(item, props.query),
		}),
		[props.scorer, props.query],
	);

	const hasMatches = categories.some(
		(category) =>
			scorer.category(category) + category.items.map(scorer.item).reduce((p, c) => p + c, 0) > 0,
	);

	const onCreateItem = useCallback((item: U) => {
		setCategories((items) => {
			const copy = items.slice();
			const index = copy.findIndex(({ id }) => id === item.category.id);
			copy[index] = {
				...copy[index],
				items: copy[index].items.concat([item]),
			};
			return copy;
		});
	}, []);

	const onUpdateItem = useCallback((item: U) => {
		setCategories((items) => {
			const copy = items.slice();
			const index = copy.findIndex(({ id }) => id === item.category.id);
			copy[index] = {
				...copy[index],
				items: copy[index].items.map((existing) => (existing.id === item.id ? item : existing)),
			};
			return copy;
		});
	}, []);

	return (
		<DndContext
			modifiers={active?.startsWith('category:') ? [restrictToVerticalAxis] : []}
			collisionDetection={closestCorners}
			sensors={sensors}
			onDragStart={onDragStart}
			onDragOver={onDragOver}
			onDragEnd={onDragEnd}
		>
			<SortableContext
				items={categories.flatMap(({ id, items }) =>
					[`category:${id}`]
						.concat(items.map(({ id }) => `item:${id}`))
						.concat([`$placeholder:${id}`]),
				)}
				strategy={() => null}
			>
				{!props.archived && (
					<Flex pl={3} pr={4} mt={3} mb={2}>
						<CreateLibraryCategory
							mutations={props.mutations}
							onCreate={(category) => {
								setCategories((categories) =>
									categories.concat([
										{
											...category,
											items: [],
										},
									]),
								);
							}}
						/>
					</Flex>
				)}
				{categories.map(
					(category) =>
						scorer.category(category) + category.items.map(scorer.item).reduce((p, c) => p + c, 0) >
							0 && (
							<LibraryCategory.Draggable
								key={category.id}
								loading={reorderingCategories[category.id]}
								query={props.query}
								category={category}
								loadingItems={loadingItems}
								items={category.items}
								mutations={props.mutations}
								scorer={scorer}
								renderer={props.renderer}
								onCreateItem={onCreateItem}
								onUpdateItem={onUpdateItem}
								onForgetItem={onForgetItem}
								onUpdateCategory={onUpdateCategory}
								onForgetCategory={onForgetCategory}
								placeholder={props.placeholder}
							/>
						),
				)}
				{props.query?.trim() && !hasMatches && (
					<Flex justifyContent="center" py={4} px={3}>
						<Empty icon={<SearchIcon />} title={t('settings.feedbackLibrary.search.empty')} />
					</Flex>
				)}
			</SortableContext>
			<DragOverlay
				dropAnimation={null}
				zIndex={999}
				style={{
					cursor: 'grabbing',
				}}
			>
				{active?.startsWith('item:') ? (
					<LibraryItem.DragOverlay
						dragging
						embeddedHandle={props.renderer.embeddedHandle}
						item={
							categories
								.flatMap((category) => category.items)
								.find(({ id }) => `item:${id}` === active)!
						}
						renderCard={props.renderer.card}
					/>
				) : (
					active?.startsWith('category:') && (
						<LibraryCategory.DragOverlay
							dragging
							mutations={props.mutations}
							category={categories.find(({ id }) => `category:${id}` === active)!}
							items={categories.find(({ id }) => `category:${id}` === active)!.items}
							scorer={scorer}
							renderer={props.renderer}
							onUpdateCategory={() => {}}
							onForgetCategory={() => {}}
						/>
					)
				)}
			</DragOverlay>
		</DndContext>
	);
}

function InlineLibrary<
	T extends { id: string; name?: string; order: number; isArchived: boolean },
	U extends { id: string; order: number; category: T },
>(props: {
	categories?: T[];
	items?: U[];
	placeholder?: Partial<U>;
	query?: string;
	archived?: boolean;
	scorer: LibraryScorer<T, U>;
	renderer: LibraryRenderer<T, U>;
	mutations: LibraryMutations<T, U>;
}) {
	if (props.categories && props.items) {
		return <LibraryWithData categories={props.categories} items={props.items} {...props} />;
	} else {
		return (
			<Skeleton.Repeat count={3}>
				<LibraryCategory.Skeleton mutations={props.mutations} />
			</Skeleton.Repeat>
		);
	}
}

export function Library<
	T extends { id: string; order: number; isArchived: boolean },
	U extends { id: string; order: number; category: T },
>(props: {
	inline?: boolean;
	categories?: T[];
	items?: U[];
	placeholder?: Partial<U>;
	scorer: LibraryScorer<T, U>;
	renderer: LibraryRenderer<T, U>;
	mutations: LibraryMutations<T, U>;
	archived?: boolean;
	onToggleArchive?: (value: boolean) => void;
}) {
	const { t } = useTranslation();

	const [query, setQuery] = useState('');

	return (
		<LibraryItemLayoutScope.Provider value="library">
			<LocalizationScope>
				<Flex px={props.inline ? 3 : 4} my={props.inline ? 0 : 4} alignItems="center" mb="48px">
					<Box flexGrow={1} flexShrink={1} minWidth={0} mr={2}>
						<Input
							search
							icon={<SearchIcon />}
							placeholder={t('settings.feedbackLibrary.search.placeholder')}
							value={query}
							onChange={setQuery}
							clearable={!!query}
						/>
					</Box>
					{props.inline !== true && (
						<Flex flexGrow={0} flexShrink={0} alignItems="center">
							{props.onToggleArchive && (
								<Box mr={4}>
									<Switch
										on={props.archived ?? false}
										onChange={(on) => props.onToggleArchive?.(on)}
										label="Toon gearchiveerd"
									/>
								</Box>
							)}
							<LanguageSelect.Scope />
						</Flex>
					)}
				</Flex>
				<Box px={props.inline ? 0 : 3}>
					<InlineLibrary {...props} query={query} />
				</Box>
			</LocalizationScope>
		</LibraryItemLayoutScope.Provider>
	);
}

Library.Category = LibraryCategory;
Library.Item = LibraryItem;
